import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ColorBackgroundPicker — equip a colored profile background using a
 * Phoenix Feather earned in the Nest store.
 *
 * Each color slot shows how many Feathers of that color the user owns.
 * Equipping consumes 1 Feather (one-way cosmetic spend). Unequipping is free.
 * The equipped color is applied via user.profile_bg_color and read by
 * /profile + /u/handle pages.
 */
export default function ColorBackgroundPicker() {
  const { user, refreshUser } = useAuth();
  const [options, setOptions] = useState([]);
  const [equipped, setEquipped] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API}/profile/colors`);
      setOptions(data?.options || []);
      setEquipped(data?.equipped || null);
    } catch {
      setOptions([]);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  async function equip(color) {
    if (busy) return;
    setBusy(color);
    try {
      await axios.post(`${API}/profile/equip-color`, { color });
      toast.success(`Background equipped — ${color.toUpperCase()} active`);
      await load();
      await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't equip color.");
    } finally {
      setBusy(null);
    }
  }

  async function unequip() {
    setBusy("unequip");
    try {
      await axios.post(`${API}/profile/unequip-color`);
      toast.success("Default background restored.");
      await load();
      await refreshUser();
    } catch (e) {
      toast.error("Couldn't reset.");
    } finally {
      setBusy(null);
    }
  }

  if (!user) return null;

  return (
    <section data-testid="color-bg-picker" className="bg-[#0F0F0F] border border-zinc-900 p-5 sm:p-6 mb-6">
      <div className="flex items-baseline justify-between gap-4 flex-wrap mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
            🪶 Profile Background Color
          </div>
          <p className="text-xs text-neutral-400 mt-1">
            Equip a color by spending one matching Phoenix Feather. Buy more in the{" "}
            <Link to="/nest" className="text-[#D4AF37] hover:underline">Nest store</Link>.
          </p>
        </div>
        {equipped && (
          <button
            onClick={unequip}
            disabled={busy === "unequip"}
            data-testid="color-unequip-btn"
            className="text-[10px] uppercase tracking-widest text-neutral-400 hover:text-[#DC2626] disabled:opacity-50"
          >
            {busy === "unequip" ? "Resetting…" : "Reset to Default"}
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {options.map((opt) => {
          const owned = opt.owned > 0;
          const isEquipped = opt.equipped;
          return (
            <div
              key={opt.color}
              data-testid={`color-tile-${opt.color}`}
              className={`relative border-2 p-3 flex flex-col items-center gap-2 ${
                isEquipped ? "border-[#D4AF37]" : owned ? "border-zinc-700" : "border-zinc-900 opacity-60"
              }`}
              style={{ background: `linear-gradient(135deg, ${opt.from}, ${opt.to})` }}
            >
              {isEquipped && (
                <div className="absolute top-1 right-1 bg-[#D4AF37] text-black text-[9px] font-bold px-1.5 py-0.5 uppercase tracking-widest">
                  Active
                </div>
              )}
              <div
                className="w-8 h-8 rounded-full border-2 border-white/30"
                style={{ background: opt.hex }}
              />
              <div className="text-xs text-white font-bold text-center">{opt.label}</div>
              <div className="text-[10px] uppercase tracking-widest text-white/70">
                {owned ? `${opt.owned} 🪶 owned` : "Not owned"}
              </div>
              <button
                onClick={() => equip(opt.color)}
                disabled={!owned || isEquipped || busy === opt.color}
                data-testid={`color-equip-${opt.color}`}
                className="w-full bg-black/40 hover:bg-black/60 disabled:bg-black/20 disabled:cursor-not-allowed text-white font-bold uppercase tracking-widest text-[10px] py-1.5 border border-white/20"
              >
                {isEquipped ? "Equipped" : busy === opt.color ? "Equipping…" : owned ? "Equip (-1 🪶)" : "Need 🪶"}
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}
